/**
 * @file Settings.jsx
 * @module Settings
 * @author Ali Hussain
 * @date 10/07/2023
 * @description This file contains the code for the settings page
 */
import { useEffect, useState } from "react";
import { Table, Button, Form, InputGroup, Spinner, Modal, Col, Row, Card } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import Api from "../../api/authApi"; // your backend API functions
import { set } from "react-hook-form";
import Confirm from "../Confirm";
import { SystemAdmin, ROLES } from "../../constants/CONSTANT";
import PubSub from "pubsub-js";
import { Link } from "react-router-dom";
export default function Index() {
  const [isRefresh, setIsRefresh] = useState(false);
  return (
    <div className="container py-4">
      <div className="d-flex flex-column gap-4">
        <Roles isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
        <UserRoleAssignment isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
        <Permissions isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
        <RolePermissionMatrix isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
      </div>
    </div>
  );
}

/* ----------------- Roles ----------------- */
export function Roles({ isRefresh, setIsRefresh }) {
  const [roles, setRoles] = useState([]);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [loading, setLoading] = useState(true);

  const [show, setShow] = useState(false);
  const [role, setRole] = useState({ name: "" });
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState();

  const handleClose = () => setShow(false);
  const handleShow = () => {
    setRole({ name: "" });
    setShow(true);
  };
  const handleChange = (e) => setRole({ ...role, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await Api.createRole(role);
    if (result.success) {
      handleClose();
      init();
      setIsRefresh(!isRefresh);
    }
  };

  const init = async () => {
    setLoading(true);
    try {
      const result = await Api.fetchRoles();
      const uniqueRecordMap = {};
      result.forEach((p) => (uniqueRecordMap[p.id] = p.name));
      setRoles(Object.keys(uniqueRecordMap).map((id) => ({ id, name: uniqueRecordMap[id] })));
    } catch (err) {
      console.error("Error loading data", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { init(); }, [isRefresh]);

  const startEdit = (record) => {
    setEditingRoleId(record.id);
    setEditingName(record.name);
  };

  const saveEdit = async (id) => {
    try {
      await Api.updateRoleById({ id, name: editingName });
      setEditingRoleId(null);
      init();
      setIsRefresh(!isRefresh);
    } catch (e) {
      console.error("Edit failed:", e);
    }
  };
  const showDeleteConfirm = (record) => {
    setShowDeleteModel(true);
    setDeleteRecord(record);
  };
  const deleteRole = async () => {
    console.log('first', deleteRecord);
    if (deleteRecord) {
      try {
        await Api.deleteRoleById(deleteRecord.id);
        setRoles((prev) => prev.filter((r) => r.id !== deleteRecord.id));
        setIsRefresh(!isRefresh);
        setShowDeleteModel(false);
      } catch (e) {
        console.error("Delete failed:", e);
      }
    } else {
      console.log('delete record is null');
    }
  };

  return (
    <Card className="shadow-sm rounded-3">
      <Card.Header className="d-flex justify-content-between align-items-center bg-custom text-white">
        <h5 className="mb-0">Roles</h5>
        <Button size="sm" variant="success" onClick={handleShow}>
          <FaPlus className="me-1" /> Create
        </Button>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" /> <p className="mt-2">Loading roles</p>
          </div>
        ) : roles.length === 0 ? (
          <div className="text-center text-muted py-5">No roles found.</div>
        ) : (
          <Table hover responsive className="align-middle shadow-sm">
            <thead className="table-light">
              <tr>
                <th className="bg-custom text-white">Role</th>
                <th className="bg-custom text-white text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>
                    {editingRoleId === role.id ? (
                      <InputGroup size="sm">
                        <Form.Control
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                        />
                        <Button variant="success" onClick={() => saveEdit(role.id)}>
                          <FaCheck />
                        </Button>
                        <Button variant="secondary" onClick={() => setEditingRoleId(null)}>
                          <FaTimes />
                        </Button>
                      </InputGroup>
                    ) : (
                      role.name
                    )}
                  </td>
                  <td className="text-center">
                    {editingRoleId !== role.id && (
                      <>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          disabled={role.name == SystemAdmin.name}
                          onClick={() => startEdit(role)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          // disabled={role.name == SystemAdmin.name}
                          disabled={true}
                          onClick={() => showDeleteConfirm(role)}
                        >
                          <FaTrash />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Modal */}
      <Modal show={show} onHide={handleClose} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title>Create Permission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="PermissionName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                placeholder="Enter role name"
                name="name"
                value={role.name}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group controlId="PermissionName">
              <Form.Label>Description</Form.Label>
              <Form.Control
                required
                as="textarea"
                placeholder="Enter description"
                name="description"
                value={role.description}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>Close</Button>
            <Button variant="success" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Confirm
        show={showDeleteModel}
        onHide={() => setShowDeleteModel(false)}
        title="Delete Role"
        message="Are you sure you want to delete this role?"
        table="role"
        deleteRole={deleteRole}
      />
    </Card>
  );
}


export function UserRoleAssignment({ isRefresh, setIsRefresh }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const init = async () => {
    setLoading(true);
    try {
      const result = await Api.fetchUserRoles(); // should return [{id, name, email, roleId}]
      const rolesResult = await Api.fetchRoles(); // [{id, name}]
      Array.isArray(result) && setUsers(result);
      Array.isArray(rolesResult) && setRoles(rolesResult);
    } catch (err) {
      console.error("Error loading data", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userRoleId, userId, roleId) => {
    try {
      await Api.updateUserRoleById({ id: userRoleId, roleid: roleId });
      // backend will upsert into userrole table
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, roleid: roleId } : u))
      );
      setIsRefresh(!isRefresh);
    } catch (e) {
      console.error("Failed to assign role", e);
    }
  };

  useEffect(() => {
    init();
  }, [isRefresh]);

  const handleUserRoleChange = async (userId, userRole) => {
    try {
      await Api.updateUserById({ id: userId, userrole: userRole });
      setIsRefresh(!isRefresh);
    } catch (e) {
      console.error("Failed to assign role", e);
    }
  };

  return (
    <Card className="shadow-sm rounded-3 mt-3">
      <Card.Header className="d-flex justify-content-between align-items-center bg-custom text-white">
        <h5 className="mb-0">User Role Management</h5>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" /> <p className="mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center text-muted py-5">No users found.</div>
        ) : (
          <Table hover responsive className="align-middle shadow-sm">
            <thead className="table-light">
              <tr>
                <th className="bg-custom text-white">User</th>
                <th className="bg-custom text-white">Email</th>
                <th className="bg-custom text-white">Type</th>
                <th className="bg-custom text-white text-center">Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.firstname + " " + user.lastname}
                  </td>
                  <td>{user.email}</td>
                  {/* <td>{user.userrole}</td> */}
                  <td>
                    {
                      <Form.Select
                        size="sm"
                        value={user.userrole || ""}
                        disabled={user.userrole == SystemAdmin.role}
                        onChange={(e) => handleUserRoleChange(user.userid, e.target.value)}
                      >
                        <option value="">-- Select Role --</option>
                        {ROLES.map((role, index) => (
                          <option key={index} value={role} disabled={role == SystemAdmin.role}>
                            {role}
                          </option>
                        ))}
                      </Form.Select>
                    }
                  </td>
                  <td className="text-center" style={{ minWidth: "200px" }}>
                    <Form.Select
                      size="sm"
                      value={user.roleid || ""}
                      disabled={user.roleid == SystemAdmin.id}
                      onChange={(e) => handleRoleChange(user.user_role_id, user.id, e.target.value)}
                    >
                      <option value="">-- Select Role --</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id} disabled={role.id == SystemAdmin.id}>
                          {role.name}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

/* ----------------- PERMISSIONS ----------------- */
export function Permissions({ isRefresh, setIsRefresh }) {
  const [permissions, setPermissions] = useState([]);
  const [editingPermissionId, setEditingPermissionId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [loading, setLoading] = useState(true);

  const [show, setShow] = useState(false);
  const [permission, setPermission] = useState({ name: "" });
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState();
  const handleClose = () => setShow(false);
  const handleShow = () => {
    setPermission({ name: "" });
    setShow(true);
  };
  const handleChange = (e) => setPermission({ ...permission, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await Api.createPermission(permission);
    if (result.success) {
      handleClose();
      init();
      setIsRefresh(!isRefresh);
    }
  };

  const init = async () => {
    setLoading(true);
    try {
      const permsRes = await Api.fetchPermissions();
      const uniquePermMap = {};
      permsRes.forEach((p) => (uniquePermMap[p.id] = p.name));
      setPermissions(Object.keys(uniquePermMap).map((id) => ({ id, name: uniquePermMap[id] })));
    } catch (err) {
      console.error("Error loading data", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { init(); }, [isRefresh]);

  const startEdit = (perm) => {
    setEditingPermissionId(perm.id);
    setEditingName(perm.name);
  };

  const saveEdit = async (id) => {
    try {
      const result = await Api.updatePermissionById({ id, name: editingName });
      if (result?.success) {
        setEditingPermissionId(null);
        init();
        setIsRefresh(!isRefresh);
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Record Update",
          message: `${result?.message ? result?.message : "Record updated successfully"
            }`,
        });
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error found",
          message: `${result?.errors ? result?.errors : "Some error found"
            }`,
        });
      }

    } catch (e) {
      console.error("Edit failed:", e);
    }
  };

  const showDeleteConfirm = (record) => {
    setShowDeleteModel(true);
    setDeleteRecord(record);
  };
  const deletePermission = async () => {
    if (deleteRecord) {
      try {
        const result = await Api.deletePermissionById(deleteRecord.id);
        if (result?.success) {
          setPermissions((prev) => prev.filter((r) => r.id !== deleteRecord.id));
          setShowDeleteModel(false);
          setIsRefresh(!isRefresh);
          PubSub.publish("RECORD_SAVED_TOAST", {
            title: "Record Deleted",
            message: `${result?.message ? result?.message : "Record deleted successfully"
              }`,
          });
        }
        else {
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Error found",
            message: `${result?.errors ? result?.errors : "Some error found"
              }`,
          });
        }
      } catch (e) {
        console.error("Delete failed:", e);
      }
    } else {
      console.log('delete record is null');
    }
  };



  return (
    <Card className="shadow-sm rounded-3">
      <Card.Header className="d-flex justify-content-between align-items-center bg-custom text-white">
        <h5 className="mb-0">Permissions</h5>
        {/* <Button size="sm" variant="success" onClick={handleShow}>
          <FaPlus className="me-1" /> Create
        </Button> */}
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" /> <p className="mt-2">Loading permissions…</p>
          </div>
        ) : permissions.length === 0 ? (
          <div className="text-center text-muted py-5">No permissions found.</div>
        ) : (
          <Table hover responsive className="align-middle shadow-sm">
            <thead className="table-light">
              <tr>
                <th className="bg-custom text-white">Permission</th>
                <th className="bg-custom text-white text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((perm) => (
                <tr key={perm.id}>
                  <td>
                    {editingPermissionId === perm.id ? (
                      <InputGroup size="sm">
                        <Form.Control
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                        />
                        <Button variant="success" onClick={() => saveEdit(perm.id)}>
                          <FaCheck />
                        </Button>
                        <Button variant="secondary" onClick={() => setEditingPermissionId(null)}>
                          <FaTimes />
                        </Button>
                      </InputGroup>
                    ) : (
                      perm.name
                    )}
                  </td>
                  <td className="text-center">
                    {editingPermissionId !== perm.id && (
                      <>
                        {/* <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          disabled={perm.name == "MODIFY_ALL" || perm.name == "DELETE_ALL" || perm.name == "VIEW_ALL"}
                          onClick={() => startEdit(perm)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={perm.name == "MODIFY_ALL" || perm.name == "DELETE_ALL" || perm.name == "VIEW_ALL"}
                          onClick={() => showDeleteConfirm(perm)}
                        >
                          <FaTrash />
                        </Button> */}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Modal */}
      <Modal show={show} onHide={handleClose} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title>Create Permission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="PermissionName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                placeholder="Enter permission name"
                name="name"
                value={permission.name}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>Close</Button>
            <Button variant="success" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Confirm
        show={showDeleteModel}
        onHide={() => setShowDeleteModel(false)}
        title="Delete Permission"
        message="Are you sure you want to delete this permission?"
        table="permission"
        deletePermission={deletePermission}
      />
    </Card>
  );
}

/* ----------------- ROLE–PERMISSION MATRIX ----------------- */
export function RolePermissionMatrix({ isRefresh, setIsRefresh }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes, rolePermsRes] = await Promise.all([
        Api.fetchRoles(),
        Api.fetchPermissions(),
        Api.fetchRolePermissions(),
      ]);

      const uniquePermMap = {};
      permsRes.forEach((p) => (uniquePermMap[p.id] = p.name));
      setPermissions(Object.keys(uniquePermMap).map((id) => ({ id, name: uniquePermMap[id] })));

      const init = {};
      rolesRes.forEach((role) => {
        init[role.id] = rolePermsRes.filter((p) => p.roleid === role.id).map((p) => p.permissionid);
      });
      setRoles(rolesRes);
      setMatrix(init);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, [isRefresh]);

  const togglePermission = async (roleId, permissionId) => {
    const hasPerm = matrix[roleId]?.includes(permissionId);
    if (hasPerm) {
      await Api.removeRolePermission(roleId, permissionId);
      setMatrix((prev) => ({
        ...prev,
        [roleId]: prev[roleId].filter((pid) => pid !== permissionId),
      }));
    } else {
      await Api.addRolePermission(roleId, permissionId);
      setMatrix((prev) => ({
        ...prev,
        [roleId]: [...(prev[roleId] || []), permissionId],
      }));
    }
  };

  return (
    <Card className="shadow-sm rounded-3">
      <Card.Header className="bg-custom text-white">
        <h5 className="mb-0">Role – Permission Matrix</h5>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" /> <p className="mt-2">Loading roles…</p>
          </div>
        ) : (
          <Table hover responsive className="align-middle text-center shadow-sm">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: 150 }} className="bg-custom text-white">Role</th>
                {permissions.map((perm) => (
                  <th className="bg-custom text-white" key={perm.id}>{perm.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td className="fw-semibold">{role.name}</td>
                  {permissions.map((perm) => (
                    <td key={perm.id}>
                      <Form.Check
                        disabled={role.name == SystemAdmin.name}
                        type="checkbox"
                        checked={matrix[role.id]?.includes(perm.id) || false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if ((perm.name?.includes('MODIFY') || perm.name?.includes('DELETE'))) {
                              const RolePermissions = matrix[role.id];
                              const permissionsRoleHave = permissions.filter((p) => RolePermissions?.includes(p.id))
                              if (permissionsRoleHave.find((p) => p.name?.includes(`VIEW_${perm.name?.split('_')[1]}`))) {
                                togglePermission(role.id, perm.id);
                              } else {
                                PubSub.publish('RECORD_ERROR_TOAST', {
                                  title: 'Permission Required',
                                  message: `Permission VIEW_${perm.name?.split('_')[1]} is required to modify this record`,
                                })
                              }
                            }
                            else {
                              togglePermission(role.id, perm.id);
                            }

                          } else {
                            if ((perm.name?.includes('VIEW'))) {
                              const RolePermissions = matrix[role.id];
                              const permissionsRoleHave = permissions.filter((p) => RolePermissions?.includes(p.id))
                              permissionsRoleHave.filter((p) => p.name?.includes(`MODIFY_${perm.name?.split('_')[1]}`) || p.name?.includes(`DELETE_${perm.name?.split('_')[1]}`)).forEach((p) => {
                                togglePermission(role.id, p.id);
                              })
                              togglePermission(role.id, perm.id);
                            } else {
                              togglePermission(role.id, perm.id);
                            }
                          }
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

